import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  RotateCcw,
  X,
} from "lucide-react";
import AdminAPI from "../../../services/AdminAPI";
import { classLabel, describeHistoryEntry, formatWhen, metaFor, roleLabel } from "./approvalMeta";

const TABS = [
  { key: "plan", label: "Plan" },
  { key: "changes", label: "Changes" },
  { key: "discussion", label: "Discussion" },
  { key: "history", label: "History" },
];

const STEPS = [
  { key: "teacher_draft", label: "Teacher" },
  { key: "hod_review", label: "HOD" },
  { key: "admin_review", label: "Admin" },
  { key: "approved", label: "Approved" },
];

function Stepper({ stage }) {
  const current = Math.max(0, STEPS.findIndex((s) => s.key === stage));
  return (
    <ol className="flex items-center gap-2 text-[11px]" aria-label="Approval progress">
      {STEPS.map((step, i) => {
        const done = i < current || stage === "approved";
        const active = i === current && stage !== "approved";
        return (
          <li key={step.key} className="flex items-center gap-2">
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold ${
                done
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : active
                  ? "border-[#1565c0] bg-[#1565c0] text-white"
                  : "border-slate-300 bg-white text-slate-400"
              }`}
            >
              {done ? "✓" : i + 1}
            </span>
            <span className={active ? "font-semibold text-[#1565c0]" : done ? "text-emerald-700" : "text-slate-400"}>
              {step.label}
            </span>
            {i < STEPS.length - 1 && <span className="h-px w-5 bg-slate-300" />}
          </li>
        );
      })}
    </ol>
  );
}

function ChipList({ label, items }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="mt-1.5">
      <span className="mr-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {list.map((item) => (
        <span key={item} className="mr-1.5 mt-1 inline-block rounded-full border border-[#d9e7f7] bg-white px-2 py-0.5 text-[11px] text-[#1565c0]">
          {item}
        </span>
      ))}
    </div>
  );
}

function ChapterBlock({ chapter }) {
  const topics = chapter.topics || [];
  return (
    <div className="rounded-xl border border-[#e6edf8] bg-white p-3">
      <div className="text-[13px] font-semibold text-slate-800">
        {chapter.chapterNo ? `${chapter.chapterNo}. ` : ""}
        {chapter.chapterName || <span className="text-slate-400">Untitled chapter</span>}
      </div>
      {topics.length > 0 && (
        <ul className="mt-1.5 list-disc pl-5 text-[12px] text-slate-600">
          {topics.map((t, i) => (
            <li key={t.id || i}>
              {t.topicName}
              {(t.subtopics || []).length > 0 && <span className="text-slate-400"> ({t.subtopics.join(", ")})</span>}
            </li>
          ))}
        </ul>
      )}
      <ChipList label="Objectives" items={chapter.learningObjectives} />
      <ChipList label="Competencies" items={[...(chapter.competencies || []), ...(chapter.customCompetencies || [])]} />
      <ChipList label="Pedagogy" items={[...(chapter.pedagogies || []), ...(chapter.customPedagogies || [])]} />
    </div>
  );
}

function PlanTab({ plan }) {
  const terms = (plan.terms || []).filter((t) => (t.units || []).length > 0);
  const [open, setOpen] = useState(() => new Set(terms.slice(0, 1).map((t) => t.termNo)));
  if (!terms.length) {
    return <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-500">This plan has no units yet.</div>;
  }
  const toggle = (n) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          className="text-[12px] font-medium text-[#1565c0] hover:underline"
          onClick={() => setOpen(open.size === terms.length ? new Set() : new Set(terms.map((t) => t.termNo)))}
        >
          {open.size === terms.length ? "Collapse all" : "Expand all"}
        </button>
      </div>
      {terms.map((term) => {
        const isOpen = open.has(term.termNo);
        const chapterCount = term.units.reduce((n, u) => n + (u.chapters || []).length, 0);
        return (
          <section key={term.termNo} className="rounded-2xl border border-[#e6edf8] bg-[#f9fbff]">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-left"
              onClick={() => toggle(term.termNo)}
              aria-expanded={isOpen}
            >
              <span className="text-[14px] font-bold text-[#1565c0]">Term {term.termNo}</span>
              <span className="flex items-center gap-2 text-[12px] text-slate-500">
                {term.units.length} unit{term.units.length === 1 ? "" : "s"} · {chapterCount} chapter{chapterCount === 1 ? "" : "s"}
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </span>
            </button>
            {isOpen && (
              <div className="space-y-4 px-4 pb-4">
                {term.units.map((unit, ui) => (
                  <div key={unit.id || ui}>
                    <div className="mb-2 text-[13px] font-semibold text-slate-700">
                      Unit {unit.unitNo || ui + 1}
                      {unit.unitName ? `: ${unit.unitName}` : ""}
                    </div>
                    <div className="space-y-2">
                      {(unit.chapters || []).map((ch, ci) => (
                        <ChapterBlock key={ch.id || ci} chapter={ch} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function ChangesTab({ detail }) {
  const changes = detail.changes_since_approved || [];
  if (!detail.has_approved_snapshot) {
    return <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-500">This plan has never been approved, so there is nothing to compare against.</div>;
  }
  if (!changes.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-[13px] text-emerald-800">
        <CheckCircle2 className="h-4 w-4" /> No changes since the last approval.
      </div>
    );
  }
  const show = (v) => (Array.isArray(v) ? v.map((x) => (typeof x === "object" ? x.topicName || JSON.stringify(x) : x)).join(", ") : v) || "—";
  return (
    <div className="space-y-3">
      {changes.map((c) => (
        <div key={c.path} className="rounded-xl border border-[#e6edf8] bg-white p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-semibold text-slate-800">{c.path}</span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                c.change === "added" ? "bg-emerald-50 text-emerald-700" : c.change === "removed" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-800"
              }`}
            >
              {c.change}
            </span>
          </div>
          {(c.fields || []).map((f) => (
            <div key={f.field} className="mt-2 grid grid-cols-1 gap-2 text-[12px] sm:grid-cols-2">
              <div className="rounded-lg bg-rose-50 p-2 text-rose-900">
                <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700">Before · {f.field}</div>
                {show(f.before)}
              </div>
              <div className="rounded-lg bg-emerald-50 p-2 text-emerald-900">
                <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Now · {f.field}</div>
                {show(f.after)}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const THREAD_TONE = {
  teacher: "border-blue-200 bg-blue-50",
  hod: "border-amber-200 bg-amber-50",
  admin: "border-emerald-200 bg-emerald-50",
};

function DiscussionTab({ thread }) {
  if (!thread.length) {
    return <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-500">No comments yet. Reasons given with a decision appear here.</div>;
  }
  return (
    <div className="space-y-2">
      {thread.map((m, i) => (
        <div key={`${m.timestamp}-${i}`} className={`rounded-xl border p-3 ${THREAD_TONE[m.role] || "border-slate-200 bg-slate-50"}`}>
          <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
            <span className="font-semibold text-slate-700">{roleLabel(m.role)}{m.actor ? ` · ${m.actor}` : ""}</span>
            <span>{formatWhen(m.timestamp)}</span>
          </div>
          <div className="whitespace-pre-wrap text-[13px] text-slate-800">{m.text}</div>
        </div>
      ))}
    </div>
  );
}

function HistoryTab({ history }) {
  if (!history.length) {
    return <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-500">No recorded activity yet.</div>;
  }
  const rows = [...history].reverse();
  return (
    <ol className="relative ml-2 border-l border-slate-200">
      {rows.map((h, i) => (
        <li key={`${h.at}-${i}`} className="mb-4 ml-4">
          <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full border border-white bg-[#1565c0]" />
          <div className="text-[13px] font-semibold text-slate-800">{describeHistoryEntry(h)}</div>
          <div className="text-[11px] text-slate-500">
            {h.actor_name || "Unknown"} · {roleLabel(h.role)} · {formatWhen(h.at)}
            {h.revision ? ` · r${h.revision}` : ""}
            {h.batch_id ? " · bulk" : ""}
            {h.seeded ? " · imported" : ""}
          </div>
          {h.reason && <div className="mt-1 rounded-lg bg-slate-50 p-2 text-[12px] text-slate-700">{h.reason}</div>}
        </li>
      ))}
    </ol>
  );
}

/**
 * Right-hand drawer for reviewing one Module 6 plan. Every decision sends the
 * revision it was loaded at, so a plan edited after load is rejected by the
 * server instead of being approved blind.
 */
export default function PlanReviewDrawer({
  uid,
  academicYear,
  role,
  onClose,
  onDecided,
  onPrev,
  onNext,
  position,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("plan");
  const [mode, setMode] = useState(null); // null | "approve" | "changes" | "override"
  const [understood, setUnderstood] = useState(false);
  const [reason, setReason] = useState("");
  const [sendBackTo, setSendBackTo] = useState("previous");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const panelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setDetail(null);
    setMode(null);
    setUnderstood(false);
    setReason("");
    setActionError("");
    setTab("plan");
    AdminAPI.acppM6PlanDetail(uid, academicYear)
      .then((res) => !cancelled && setDetail(res))
      .catch((e) => !cancelled && setError(e.message || "Could not load this plan."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [uid, academicYear]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const summary = detail?.summary;
  const meta = useMemo(() => (summary ? metaFor(summary.display_status, summary.workflow_stage) : null), [summary]);
  const isAdminGate = summary?.workflow_stage === "admin_review" && role !== "hod";

  const submit = async () => {
    if (!summary) return;
    if (mode === "changes" && !reason.trim()) {
      setActionError("Tell the teacher what to change. A reason is required.");
      return;
    }
    if (mode === "override" && (!reason.trim() || !understood)) {
      setActionError("Explain why the HOD step is being skipped and tick the confirmation box.");
      return;
    }
    setBusy(true);
    setActionError("");
    try {
      const res = await AdminAPI.acppM6Review(summary.uid, {
        academic_year: academicYear || undefined,
        action: mode === "approve" ? "approve" : mode === "override" ? "override_hod" : "request_changes",
        confirm_override: mode === "override",
        reason: reason.trim(),
        send_back_to: sendBackTo,
        expected_revision: summary.revision,
      });
      onDecided(res, mode);
    } catch (e) {
      setActionError(e.message || "The decision could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const canDecide = summary && (summary.can_approve || summary.can_request_changes || summary.can_override_hod);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Review plan">
      <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Close review" onClick={() => !busy && onClose()} />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl outline-none"
        style={{ fontFamily: "Poppins, system-ui, sans-serif" }}
      >
        <header className="border-b border-[#e6edf8] bg-gradient-to-br from-white to-[#f4f8fe] px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {summary ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="m-0 text-[18px] font-bold text-slate-900">
                      Grade {classLabel(summary)} · {summary.subject}
                    </h2>
                    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${meta.pill}`}>{meta.label}</span>
                    {summary.changed_since_approved && (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                        Edited since approval
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[12px] text-slate-500">
                    {summary.teacher_name || "Unassigned teacher"}
                    {summary.hod_name ? ` · HOD ${summary.hod_name}` : ""} · revision {summary.revision}
                    {summary.submitted_at ? ` · submitted ${formatWhen(summary.submitted_at)}` : ""}
                  </p>
                </>
              ) : (
                <h2 className="m-0 text-[18px] font-bold text-slate-900">Review plan</h2>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {position && <span className="mr-1 text-[11px] text-slate-500">{position}</span>}
              <button type="button" onClick={onPrev} disabled={!onPrev || busy} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30" aria-label="Previous plan">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={onNext} disabled={!onNext || busy} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30" aria-label="Next plan">
                <ChevronRight className="h-5 w-5" />
              </button>
              <button type="button" onClick={onClose} disabled={busy} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          {summary && (
            <div className="mt-3">
              <Stepper stage={summary.workflow_stage} />
            </div>
          )}
        </header>

        {summary?.last_changes_requested && summary.display_status === "changes_requested" && (
          <div className="flex gap-2 border-b border-rose-200 bg-rose-50 px-5 py-3 text-[12px] text-rose-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <span className="font-semibold">{summary.last_changes_requested.by}</span> asked for changes
              {summary.last_changes_requested.at ? ` · ${formatWhen(summary.last_changes_requested.at)}` : ""}:
              <div className="mt-0.5 whitespace-pre-wrap">{summary.last_changes_requested.reason}</div>
            </div>
          </div>
        )}

        <nav className="flex gap-1 border-b border-[#e6edf8] px-5" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-2.5 text-[13px] font-medium ${
                tab === t.key ? "border-[#1565c0] text-[#1565c0]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
              {t.key === "changes" && detail?.changes_since_approved?.length > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-800">
                  {detail.changes_since_approved.length}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto bg-[#f9fbff] px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading plan…
            </div>
          )}
          {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-800">{error}</div>}
          {detail && !loading && (
            <>
              {tab === "plan" && <PlanTab plan={detail.plan} />}
              {tab === "changes" && <ChangesTab detail={detail} />}
              {tab === "discussion" && <DiscussionTab thread={detail.remarks_thread || []} />}
              {tab === "history" && <HistoryTab history={detail.status_history || []} />}
            </>
          )}
        </div>

        {detail && (
          <footer className="border-t border-[#e6edf8] bg-white px-5 py-3">
            {summary.is_own_plan && (
              <p className="m-0 mb-2 text-[12px] text-slate-500">You are assigned as the teacher on this plan, so you cannot review it.</p>
            )}
            {!canDecide && !summary.is_own_plan && (
              <p className="m-0 flex items-center gap-1.5 text-[12px] text-slate-500">
                <Clock className="h-3.5 w-3.5" />
                {summary.workflow_stage === "approved"
                  ? "This plan is approved."
                  : summary.workflow_stage === "teacher_draft"
                  ? "Waiting for the teacher to submit."
                  : "Waiting on the other review stage."}
              </p>
            )}
            {summary.can_override_hod && !mode && (
              <p className="m-0 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                {summary.hod_assigned
                  ? `This plan is waiting for its HOD${summary.hod_name ? ` (${summary.hod_name})` : ""}. `
                  : "No HOD is assigned to this plan. "}
                Only if the HOD is unavailable, you can skip the HOD step for this one plan.
              </p>
            )}
            {canDecide && !mode && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {summary.can_request_changes && (
                  <button
                    type="button"
                    onClick={() => setMode("changes")}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-rose-300 bg-white px-4 py-2 text-[13px] font-semibold text-rose-700 hover:bg-rose-50"
                  >
                    <RotateCcw className="h-4 w-4" /> Request changes
                  </button>
                )}
                {summary.can_override_hod && (
                  <button
                    type="button"
                    onClick={() => setMode("override")}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2 text-[13px] font-semibold text-white hover:bg-amber-600"
                  >
                    <AlertTriangle className="h-4 w-4" /> Skip HOD approval…
                  </button>
                )}
                {summary.can_approve && (
                  <button
                    type="button"
                    onClick={() => setMode("approve")}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-4 w-4" /> {isAdminGate ? "Approve plan" : "Approve and send to admin"}
                  </button>
                )}
              </div>
            )}
            {canDecide && mode && (
              <div>
                {mode === "override" ? (
                  <>
                    <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-950">
                      <div className="mb-1 flex items-center gap-1.5 font-semibold">
                        <AlertTriangle className="h-4 w-4" /> Are you sure you want to skip the HOD approval?
                      </div>
                      This moves <strong>only this plan</strong> to admin review without the HOD's approval. It is recorded
                      in the plan's history as an admin override{summary.hod_name ? ` and ${summary.hod_name} is notified` : ""}.
                      You will still need to approve it separately.
                    </div>
                    <label className="mb-1 block text-[12px] font-semibold text-slate-700" htmlFor="m6-override-reason">
                      Why is the HOD step being skipped? <span className="text-rose-600">*</span>
                    </label>
                    <textarea
                      id="m6-override-reason"
                      rows={2}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      autoFocus
                      className="w-full rounded-xl border border-slate-300 p-2.5 text-[13px] focus:border-[#1565c0] focus:outline-none focus:ring-2 focus:ring-[#1565c0]/20"
                      placeholder="e.g. HOD is on leave until next month."
                    />
                    <label className="mt-2 flex items-start gap-2 text-[12px] text-slate-700">
                      <input type="checkbox" className="mt-0.5" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
                      I understand this skips the HOD approval for this plan only.
                    </label>
                  </>
                ) : mode === "approve" ? (
                  <p className="m-0 mb-2 text-[13px] text-slate-700">
                    {isAdminGate
                      ? "Approving finalises this plan and generates its Module 7 and Module 13 outlines."
                      : "Approving sends this plan to the admin for final approval."}
                  </p>
                ) : (
                  <>
                    <label className="mb-1 block text-[12px] font-semibold text-slate-700" htmlFor="m6-reason">
                      What needs to change? <span className="text-rose-600">*</span>
                    </label>
                    <textarea
                      id="m6-reason"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      autoFocus
                      className="w-full rounded-xl border border-slate-300 p-2.5 text-[13px] focus:border-[#1565c0] focus:outline-none focus:ring-2 focus:ring-[#1565c0]/20"
                      placeholder="Be specific so the teacher can fix it in one pass."
                    />
                    {isAdminGate && (
                      <div className="mt-2 flex gap-4 text-[12px] text-slate-700">
                        <label className="flex items-center gap-1.5">
                          <input type="radio" checked={sendBackTo === "previous"} onChange={() => setSendBackTo("previous")} /> Back to HOD
                        </label>
                        <label className="flex items-center gap-1.5">
                          <input type="radio" checked={sendBackTo === "teacher"} onChange={() => setSendBackTo("teacher")} /> Straight to teacher
                        </label>
                      </div>
                    )}
                  </>
                )}
                {actionError && <div className="mt-2 rounded-lg bg-rose-50 p-2 text-[12px] text-rose-800">{actionError}</div>}
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setMode(null);
                      setActionError("");
                    }}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={submit}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-60 ${
                      mode === "approve"
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : mode === "override"
                        ? "bg-amber-500 hover:bg-amber-600"
                        : "bg-rose-600 hover:bg-rose-700"
                    }`}
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {mode === "approve" ? "Confirm approval" : mode === "override" ? "Skip HOD and continue" : "Send back"}
                  </button>
                </div>
              </div>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
